import { Box, Button, Flex, IconFont, Popover, Tooltip, usePopoverHeight } from '@byecode/ui'
import { ActionHandleEventType, type IndependentAction } from '@lighthouse/core'
import {
    actionTypeInfos,
    buttonActions,
    flattenActionTypeInfos,
    isEmptyRichTextValue,
    isEmptyVariableValue,
    ListItem4ByecodeUi,
    useAtomData,
    validateWechatMerchantSelection
} from '@lighthouse/shared'
import React, { useMemo, useState } from 'react'

import { otherPlatformListAtom } from '@/atoms/application/state'
import { actionFlowListAtom, automationFlowListAtom } from '@/atoms/workflow/state'

import { ActionAdderConfigure } from './ActionAdderConfigure'
import { useActionExecuteFilterTooltip } from './ActionExecuteFilterTooltip'
import { ErrorTag, ExecuteFilterCount, IconGroup, StyledIconWrapper } from './styles'

interface ActionAdderProps {
    action: IndependentAction
    allParentActions: IndependentAction[]
    onChange: (val: IndependentAction) => void
    onDuplicate?: (id: string) => void
    onDelete?: (id: string) => void
    dragHandleProps?: {
        attributes?: unknown
        listeners?: unknown
        setActivatorNodeRef?: (node: HTMLElement | null) => void
    }
}

const checkParams = (action: IndependentAction) => {
    const { type, data } = action
    if (type === ActionHandleEventType.ACTION || type === ActionHandleEventType.AUTOMATION) {
        return false
    }

    if (type === ActionHandleEventType.COPY) {
        return !isEmptyRichTextValue(data.copy.copyText)
    }

    if (type === ActionHandleEventType.OPEN_LINK) {
        return !isEmptyRichTextValue(data.openLink.link)
    }

    if (type === ActionHandleEventType.OPEN_PAGE) {
        return !!data.openPage.openPageId
    }

    if (type === ActionHandleEventType.UPDATE_PAGE_SHARE) {
        const { image, title, description, link } = data.updatePageShare || {}
        return (
            !isEmptyVariableValue(image) ||
            (title && !isEmptyRichTextValue(title)) ||
            (description && !isEmptyRichTextValue(description)) ||
            (link && !isEmptyRichTextValue(link))
        )
    }

    if (type === ActionHandleEventType.WECHAT_PAY) {
        const { wechatPay } = data
        return (
            !!wechatPay?.descFieldId &&
            !!wechatPay.amountFieldId &&
            !!(wechatPay.nodeId || wechatPay.dataSourceId)
        )
    }

    const buttonAction = buttonActions[type]

    // @ts-expect-error fix data type error
    return buttonAction?.every(({ name }) => data[type][name])
}

export const ActionAdder: React.FC<ActionAdderProps> = ({ action, allParentActions, onChange, onDuplicate, onDelete, dragHandleProps }) => {
    const accounts = useAtomData(otherPlatformListAtom)
    // const appId = useCurrentAppId()
    // const envId = useCurrentEnvId()
    // const allPages = useCurrentLanguagePageList()
    const independentAction = useMemo(() => {
        return action
    }, [action])

    const { id: actionId, type, data, config } = independentAction
    const executeFilter = config?.executeFilter
    const { tooltipContent, disabled: tooltipDisabled, executeFilterLength } = useActionExecuteFilterTooltip(executeFilter)

    const comingFlowId = type === 'flow' ? data?.flow?.flowId : type === 'automation' ? data?.automation?.flowId : ''
    const actionWorkflows = useAtomData(actionFlowListAtom)
    const automationWorkflows = useAtomData(automationFlowListAtom)
    const workflows = useMemo(() => [...actionWorkflows, ...automationWorkflows], [actionWorkflows, automationWorkflows])

    const currentWorkFlow = useMemo(
        () => workflows.find(w => (type === 'flow' || type === 'automation') && comingFlowId === w.id),
        [comingFlowId, type, workflows]
    )

    const isMerchantConfigured =
        type !== ActionHandleEventType.WECHAT_PAY || validateWechatMerchantSelection('WECHAT_PAY', data.wechatPay, accounts)
    const isParamsConfigured = (currentWorkFlow ? currentWorkFlow.enabled : checkParams(independentAction)) && isMerchantConfigured

    const currentAction = useMemo(() => {
        if (type === 'flow') {
            const actionWorkflow = actionWorkflows.find(w => w.id === comingFlowId)
            if (!actionWorkflow) {
                return
            }
            return {
                type: actionWorkflow?.type,
                label: actionWorkflow?.name,
                value: actionWorkflow?.id,
                icon: 'MouseClick'
            }
        }
        if (type === 'automation') {
            const automationWorkflow = automationWorkflows.find(w => w.id === comingFlowId)
            if (!automationWorkflow) {
                return
            }
            return {
                type: automationWorkflow?.type,
                label: automationWorkflow?.name,
                value: automationWorkflow?.id,
                icon: 'FlowArrow'
            }
        }
        return flattenActionTypeInfos(actionTypeInfos).find(item => item.value === type)
    }, [actionWorkflows, automationWorkflows, comingFlowId, type])

    const [open, setOpen] = useState(false)
    const { ref, height: maxHeight } = usePopoverHeight({ opened: open })

    return (
        <Box ref={ref}>
            <Popover width={360} opened={open} position="left-start" onChange={setOpen} withinPortal>
                <Popover.Target>
                    <Box>
                        <Tooltip zIndex={201} title={tooltipContent} disabled={tooltipDisabled} placement="left">
                            <ListItem4ByecodeUi
                                styles={{
                                    root: {
                                        padding: '8px!important',
                                        '&:hover': {
                                            [`${StyledIconWrapper}`]: {
                                                background: 'var(--color-white)'
                                            },
                                            [`${ExecuteFilterCount}`]: {
                                                display: 'none'
                                            },
                                            [`${IconGroup}`]: {
                                                display: 'flex'
                                            }
                                        }
                                    }
                                }}
                                justifyContent="space-between"
                                enableHover
                                alignItems="center"
                            >
                                <Flex gap="4px" alignItems="center">
                                    <StyledIconWrapper style={{ width: 32, height: 32 }}>
                                        <IconFont type={currentAction?.icon ?? 'Forbidden'} size={20} fill="var(--color-gray-500)" />
                                        {!isParamsConfigured && (
                                            <ErrorTag type="WarningCircleFilled" size={16} fill="var(--color-red-500)" />
                                        )}
                                    </StyledIconWrapper>

                                    <Box style={{ marginLeft: 4 }}>
                                        <Flex>{currentAction?.label}</Flex>
                                    </Box>
                                </Flex>
                                <Flex alignItems="center" gap="8px">
                                    {executeFilterLength > 0 && <ExecuteFilterCount>执行条件 {executeFilterLength}</ExecuteFilterCount>}
                                    <IconGroup gap="4px" alignItems="center">
                                        <Tooltip title="创建副本">
                                            <Button
                                                type="text"
                                                icon={<IconFont type="Duplicate" size={16} fill="var(--color-gray-400)" />}
                                                onClick={e => {
                                                    e.stopPropagation()
                                                    onDuplicate?.(actionId)
                                                }}
                                                radius={5}
                                                style={{ padding: 2, width: 20, height: 20 }}
                                            />
                                        </Tooltip>
                                        <Tooltip title="删除">
                                            <Button
                                                type="text"
                                                icon={<IconFont type="Trash" size={16} fill="var(--color-gray-400)" />}
                                                onClick={e => {
                                                    e.stopPropagation()
                                                    onDelete?.(actionId)
                                                }}
                                                radius={5}
                                                style={{ padding: 2, width: 20, height: 20 }}
                                            />
                                        </Tooltip>
                                        <Box
                                            ref={dragHandleProps?.setActivatorNodeRef}
                                            // dnd-kit expects attributes/listeners to be spread on the activator element
                                            {...(dragHandleProps?.attributes as any)}
                                            {...(dragHandleProps?.listeners as any)}
                                            onClick={e => {
                                                e.stopPropagation()
                                            }}
                                            style={{ display: 'inline-flex', cursor: 'grab' }}
                                        >
                                            <IconFont type="DotsSix" size={16} fill="var(--color-gray-400)" />
                                        </Box>
                                    </IconGroup>
                                </Flex>
                            </ListItem4ByecodeUi>
                        </Tooltip>
                    </Box>
                </Popover.Target>
                <Popover.Dropdown compact style={{ maxHeight, display: 'flex', flexDirection: 'column' }}>
                    <ActionAdderConfigure
                        action={independentAction}
                        allParentActions={allParentActions}
                        onClose={() => setOpen(false)}
                        onChange={onChange}
                    />
                </Popover.Dropdown>
            </Popover>
        </Box>
    )
}
